import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import Toast from '../components/Toast';
import { useToast } from '../hooks/useToast';
import { saveRecord } from '../utils/storage';
import { validateEmail, validatePhone, validateMinLen } from '../utils/validation';
import { supabase } from '../utils/supabase';

type Tab = 'access' | 'session';

interface AccessForm {
  first_name: string; last_name: string; email_id: string; phone_number: string;
  category: string; level: string; description: string;
}
interface SessionForm {
  first_name: string; last_name: string; email_id: string; phone_number: string;
  topic: string; session_time: string; session_type: string; description: string;
}
type FormErrors<T> = Partial<Record<keyof T, string>>;

const initAccess: AccessForm  = { first_name: '', last_name: '', email_id: '', phone_number: '', category: '', level: '', description: '' };
const initSession: SessionForm = { first_name: '', last_name: '', email_id: '', phone_number: '', topic: '', session_time: '', session_type: '', description: '' };

/* ─────────────────────────────────────────────────────────────────────
   Field wrapper — MUST live outside RequestPage so React never remounts
   it on each render (which would destroy input focus on every keystroke).
───────────────────────────────────────────────────────────────────── */
interface FieldProps {
  id: string;
  label: string;
  required?: boolean;
  optionalLabel?: boolean;
  error?: string;
  hint?: string;
  children: React.ReactNode;
}
const Field: React.FC<FieldProps> = ({ id, label, required = true, optionalLabel, error, hint, children }) => (
  <div className="form-group">
    <label className="form-label" htmlFor={id}>
      {label}{' '}
      {required    && <span className="req">*</span>}
      {optionalLabel && <span style={{ color: 'var(--gray-400)' }}>(Optional)</span>}
    </label>
    {children}
    {error && <span className="form-error show">{error}</span>}
    {hint  && <span className="form-hint">{hint}</span>}
  </div>
);

/* ─── Shared option lists (module-level constants — never re-created) ─── */
const ACCESS_CATEGORIES = [
  { value: 1, label: 'AWS Cloud Practitioner (CCP)' },
  { value: 2, label: 'AWS Solutions Architect (SAA-C03)' },
  { value: 3, label: 'AWS Developer Associate (DVA)' },
  { value: 4, label: 'Python Programming' },
  { value: 5, label: 'Java Development' },
  { value: 6, label: '.NET / C# Development' },
  { value: 7, label: 'Data Structures & Algorithms' },
  { value: 8, label: 'System Design' },
  { value: 9, label: 'Full Stack Development' },
  { value: 10, label: 'Other' },
];

const EXPERIENCE_LEVELS = [
  { value: 1, label: 'Beginner (0-1 years)' },
  { value: 2, label: 'Intermediate (1-3 years)' },
  { value: 3, label: 'Advanced (3-5 years)' },
  { value: 4, label: 'Expert (5+ years)' },
];

const TRAINING_TOPICS = [
  'AWS Cloud (EC2, S3, Lambda, VPC, RDS)',
  'AWS Solutions Architecture',
  'Python Programming',
  'Java / Spring Boot',
  '.NET / C# Development',
  'C & C++ Programming',
  'Data Structures & Algorithms',
  'System Design',
  'Cloud Solution Architecture',
  'AI & Machine Learning',
  'Deep Learning & Neural Networks',
  'Generative AI & LLMs',
];

const SESSION_TYPES = [
  'Single Session (1 hour)',
  'Weekly Sessions (1 month)',
  'Intensive Bootcamp (1 week)',
  'Custom Plan',
];

const TIMING_OPTIONS = [
  'Weekday Morning (9AM–12PM)',
  'Weekday Evening (6PM–9PM)',
  'Weekend Morning',
  'Weekend Evening',
  'Flexible / Anytime',
];

const CARD_STYLE: React.CSSProperties = {
  background: '#fff',
  border: '1.5px solid var(--gray-200)',
  borderRadius: 'var(--radius-lg)',
  padding: '36px',
  boxShadow: 'var(--shadow-sm)',
};
const SUCCESS_CARD_STYLE: React.CSSProperties = {
  background: '#fff',
  border: '1.5px solid var(--green)',
  borderRadius: 'var(--radius-lg)',
  padding: '48px 36px',
  textAlign: 'center',
  boxShadow: 'var(--shadow-sm)',
};

/* ═══════════════════════════════════════════════════════════ */
const RequestPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState<Tab>('access');

  const [accessForm, setAccessForm] = useState<AccessForm>(initAccess);
  const [sessionForm, setSessionForm] = useState<SessionForm>(initSession);
  const [accessErrors, setAccessErrors]   = useState<FormErrors<AccessForm>>({});
  const [sessionErrors, setSessionErrors] = useState<FormErrors<SessionForm>>({});
  const [accessSubmitted,  setAccessSubmitted]  = useState(false);
  const [sessionSubmitted, setSessionSubmitted] = useState(false);

  const { msg, type, visible, showToast } = useToast();

  /* Pre-select tab / topic from URL params */
  useEffect(() => {
    const t     = searchParams.get('tab');
    const topic = searchParams.get('topic');
    if (t === 'session') setTab('session');
    if (topic) {
      const decoded = decodeURIComponent(topic).replace(/\+/g, ' ');
      setSessionForm(prev => ({ ...prev, topic: decoded }));
      setTab('session');
    }
  }, [searchParams]);

  /* ── Validation ── */
  const validateAccess = (): boolean => {
    const errs: FormErrors<AccessForm> = {};
    if (!accessForm.first_name.trim() || accessForm.first_name.trim().length < 2)
      errs.first_name = 'First name is required (min 2 chars).';
    if (!accessForm.last_name.trim() || accessForm.last_name.trim().length < 2)
      errs.last_name = 'Last name is required (min 2 chars).';
    if (!accessForm.email_id.trim() || !validateEmail(accessForm.email_id))
      errs.email_id = 'Please enter a valid email address.';
    if (!accessForm.phone_number.trim() || !validatePhone(accessForm.phone_number))
      errs.phone_number = 'Please enter a valid phone number.';
    if (!accessForm.category) errs.category = 'Please select a category.';
    if (!accessForm.level)    errs.level    = 'Please select your experience level.';
    setAccessErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const validateSession = (): boolean => {
    const errs: FormErrors<SessionForm> = {};
    if (!sessionForm.first_name.trim() || sessionForm.first_name.trim().length < 2)
      errs.first_name = 'First name is required (min 2 chars).';
    if (!sessionForm.last_name.trim() || sessionForm.last_name.trim().length < 2)
      errs.last_name = 'Last name is required (min 2 chars).';
    if (!sessionForm.email_id.trim() || !validateEmail(sessionForm.email_id))
      errs.email_id = 'Please enter a valid email address.';
    if (!sessionForm.phone_number.trim() || !validatePhone(sessionForm.phone_number))
      errs.phone_number = 'Please enter a valid phone number.';
    if (!sessionForm.topic)       errs.topic       = 'Please select a topic.';
    if (!sessionForm.session_type) errs.session_type = 'Please select a session type.';
    if (!sessionForm.session_time)      errs.session_time      = 'Please select a session time preference.';
    if (!sessionForm.description.trim() || !validateMinLen(sessionForm.description, 20))
      errs.description = 'Please describe your goals (at least 20 characters).';
    setSessionErrors(errs);
    return Object.keys(errs).length === 0;
  };



  /* ── Submitters ── */
  const submitAccess = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateAccess()) { showToast('⚠️ Please fix the errors before submitting.', 'error'); return; }
    
    try {

      
      const { error } = await supabase
        .from('user_requests')
        .insert([{
          first_name: accessForm.first_name,
          last_name: accessForm.last_name,
          email_id: accessForm.email_id,
          phone_number: accessForm.phone_number,
          category: parseInt(accessForm.category, 10),
          level: parseInt(accessForm.level, 10),
          description: accessForm.description || null
        }]);
      
      if (error) {
        console.error('Supabase error:', error);
        showToast('⚠️ Failed to submit request. Please try again.', 'error');
        return;
      }
      
      setAccessSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error('Error submitting access request:', err);
      showToast('⚠️ An unexpected error occurred. Please try again.', 'error');
    }
  };

  const submitSession = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateSession()) { showToast('⚠️ Please fix the errors before submitting.', 'error'); return; }
    
    try {
      const { error } = await supabase
        .from('user_sessions')
        .insert([{
          first_name: sessionForm.first_name,
          last_name: sessionForm.last_name,
          email_id: sessionForm.email_id,
          phone_number: sessionForm.phone_number,
          category: parseInt(sessionForm.topic, 10),
          session_time: sessionForm.session_time,
          session_type: sessionForm.session_type,
          description: sessionForm.description || null
        }]);
      
      if (error) {
        console.error('Supabase error:', error);
        showToast('⚠️ Failed to submit session request. Please try again.', 'error');
        return;
      }
      
      setSessionSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      console.error('Error submitting session request:', err);
      showToast('⚠️ An unexpected error occurred. Please try again.', 'error');
    }
  };

  const resetAccess  = () => { setAccessForm(initAccess); setAccessErrors({}); setAccessSubmitted(false); };
  const resetSession = () => { setSessionForm(initSession); setSessionErrors({}); setSessionSubmitted(false); };

  /* ── Generic onChange helpers — stable function references via field name ── */
  const onChangeA = (field: keyof AccessForm) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setAccessForm(prev => ({ ...prev, [field]: e.target.value }));

  const getCategoryLabel = (value: string): string => {
    const cat = ACCESS_CATEGORIES.find(c => c.value === Number(value));
    return cat ? cat.label : value;
  };

  const getLevelLabel = (value: string): string => {
    const lvl = EXPERIENCE_LEVELS.find(l => l.value === Number(value));
    return lvl ? lvl.label : value;
  };

  const onChangeS = (field: keyof SessionForm) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
      setSessionForm(prev => ({ ...prev, [field]: e.target.value }));

  /* ══════════════════════════════════════════════════════════════ */
  return (
    <Layout>
      <Toast msg={msg} type={type} visible={visible} />

      <div className="page-hero">
        <span className="section-tag">Get Started</span>
        <h1>Request Your <span className="text-orange">Learning Journey</span></h1>
        <p>Request exam access or book a personalized 1-on-1 training session with our certified experts.</p>
      </div>

      <div className="section">
        <div className="container" style={{ maxWidth: '700px' }}>

          {/* Tab buttons */}
          <div style={{ display: 'flex', gap: '6px', marginBottom: '28px' }}>
            {(['access', 'session'] as Tab[]).map(t => (
              <button
                key={t}
                onClick={() => setTab(t)}
                style={{
                  padding: '10px 22px',
                  borderRadius: 'var(--radius-sm)',
                  border: `1.5px solid ${tab === t ? 'var(--blue)' : 'var(--gray-200)'}`,
                  background: tab === t ? 'var(--blue)' : '#fff',
                  cursor: 'pointer',
                  fontWeight: 700,
                  fontSize: '0.88rem',
                  fontFamily: "'Nunito',sans-serif",
                  color: tab === t ? '#fff' : 'var(--gray-600)',
                  transition: 'all .2s',
                }}
              >
                {t === 'access' ? '🔑 Request Exam Access' : '🎯 Book Training Session'}
              </button>
            ))}
          </div>

          {/* ════════════════════════
              ACCESS REQUEST FORM
          ════════════════════════ */}
          {tab === 'access' && (
            !accessSubmitted ? (
              <div style={CARD_STYLE}>
                <h2 style={{ fontSize: '1.25rem', fontWeight: 800, marginBottom: '5px', color: 'var(--gray-800)' }}>
                  Request Exam Access
                </h2>
                <p style={{ color: 'var(--gray-500)', fontSize: '0.88rem', marginBottom: '28px' }}>
                  Fill in your details below. We'll review and send your unique access key by email within 24 hours.
                </p>

                <form onSubmit={submitAccess} noValidate>
                  <div className="form-row">
                    <Field id="acc_fname" label="First Name" error={accessErrors.first_name}>
                      <input
                        id="acc_fname"
                        type="text"
                        className={`form-control${accessErrors.first_name ? ' error' : ''}`}
                        placeholder="Raj"
                        value={accessForm.first_name}
                        onChange={onChangeA('first_name')}
                        autoComplete="given-name"
                      />
                    </Field>
                    <Field id="acc_lname" label="Last Name" error={accessErrors.last_name}>
                      <input
                        id="acc_lname"
                        type="text"
                        className={`form-control${accessErrors.last_name ? ' error' : ''}`}
                        placeholder="Kumar"
                        value={accessForm.last_name}
                        onChange={onChangeA('last_name')}
                        autoComplete="family-name"
                      />
                    </Field>
                  </div>

                  <Field id="acc_email" label="Email Address" error={accessErrors.email_id}>
                    <input
                      id="acc_email"
                      type="email"
                      className={`form-control${accessErrors.email_id ? ' error' : ''}`}
                      placeholder="raj@example.com"
                      value={accessForm.email_id}
                      onChange={onChangeA('email_id')}
                      autoComplete="email"
                    />
                  </Field>

                  <Field id="acc_phone" label="Phone Number" error={accessErrors.phone_number}>
                    <input
                      id="acc_phone"
                      type="tel"
                      className={`form-control${accessErrors.phone_number ? ' error' : ''}`}
                      placeholder="+91 98765 43210"
                      value={accessForm.phone_number}
                      onChange={onChangeA('phone_number')}
                      autoComplete="tel"
                    />
                  </Field>

                  <Field id="acc_category" label="Exam Category" error={accessErrors.category}>
                    <select
                      id="acc_category"
                      className={`form-control${accessErrors.category ? ' error' : ''}`}
                      value={accessForm.category}
                      onChange={onChangeA('category')}
                    >
                      <option value="">-- Select Category --</option>
                      {ACCESS_CATEGORIES.map(o => <option key={o.value} value={String(o.value)}>{o.label}</option>)}
                    </select>
                  </Field>

                  <Field id="acc_level" label="Current Experience Level" error={accessErrors.level}>
                    <select
                      id="acc_level"
                      className={`form-control${accessErrors.level ? ' error' : ''}`}
                      value={accessForm.level}
                      onChange={onChangeA('level')}
                    >
                      <option value="">-- Select Level --</option>
                      {EXPERIENCE_LEVELS.map(o => <option key={o.value} value={String(o.value)}>{o.label}</option>)}
                    </select>
                  </Field>

                  <Field id="acc_description" label="Why are you interested?" required={false} optionalLabel>
                    <textarea
                      id="acc_description"
                      className="form-control"
                      placeholder="I'm preparing for AWS certification and want to practice with realistic questions..."
                      value={accessForm.description}
                      onChange={onChangeA('description')}
                    />
                  </Field>

                  <button type="submit" className="btn btn-blue btn-full btn-lg">
                    Submit Access Request →
                  </button>
                </form>
              </div>
            ) : (
              <div style={SUCCESS_CARD_STYLE}>
                <div style={{ fontSize: '3.5rem', marginBottom: '14px' }}>✅</div>
                <h2 style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--gray-800)', marginBottom: '8px' }}>
                  Access Request Submitted!
                </h2>
                <p style={{ color: 'var(--gray-600)', maxWidth: '440px', margin: '0 auto 24px' }}>
                  Thank you! We've received your request and will send your unique access key to your email within{' '}
                  <strong>24 hours</strong>. Please check your inbox and spam folder.
                </p>
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                  <Link to="/login" className="btn btn-blue">Go to Login →</Link>
                  <button className="btn btn-outline-gray" onClick={resetAccess}>Submit Another</button>
                </div>
              </div>
            )
          )}

          {/* ════════════════════════
              SESSION BOOKING FORM
          ════════════════════════ */}
          {tab === 'session' && (
            !sessionSubmitted ? (
              <div style={CARD_STYLE}>
                <h2 style={{ fontSize: '1.25rem', fontWeight: 800, marginBottom: '5px', color: 'var(--gray-800)' }}>
                  Book a 1-on-1 Training Session
                </h2>
                <p style={{ color: 'var(--gray-500)', fontSize: '0.88rem', marginBottom: '28px' }}>
                  Tell us what you want to learn and we'll match you with the perfect certified instructor.
                </p>

                <form onSubmit={submitSession} noValidate>
                  <div className="form-row">
                    <Field id="ses_fname" label="First Name" error={sessionErrors.first_name}>
                      <input
                        id="ses_fname"
                        type="text"
                        className={`form-control${sessionErrors.first_name ? ' error' : ''}`}
                        placeholder="Priya"
                        value={sessionForm.first_name}
                        onChange={onChangeS('first_name')}
                        autoComplete="given-name"
                      />
                    </Field>
                    <Field id="ses_lname" label="Last Name" error={sessionErrors.last_name}>
                      <input
                        id="ses_lname"
                        type="text"
                        className={`form-control${sessionErrors.last_name ? ' error' : ''}`}
                        placeholder="Sharma"
                        value={sessionForm.last_name}
                        onChange={onChangeS('last_name')}
                        autoComplete="family-name"
                      />
                    </Field>
                  </div>

                  <Field id="ses_email" label="Email Address" error={sessionErrors.email_id}>
                    <input
                      id="ses_email"
                      type="email"
                      className={`form-control${sessionErrors.email_id ? ' error' : ''}`}
                      placeholder="priya@example.com"
                      value={sessionForm.email_id}
                      onChange={onChangeS('email_id')}
                      autoComplete="email"
                    />
                  </Field>

                  <Field id="ses_phone" label="Phone Number" error={sessionErrors.phone_number}>
                    <input
                      id="ses_phone"
                      type="tel"
                      className={`form-control${sessionErrors.phone_number ? ' error' : ''}`}
                      placeholder="+91 98765 43210"
                      value={sessionForm.phone_number}
                      onChange={onChangeS('phone_number')}
                      autoComplete="tel"
                    />
                  </Field>

                  <Field id="ses_topic" label="Training Topic" error={sessionErrors.topic}>
                    <select
                      id="ses_topic"
                      className={`form-control${sessionErrors.topic ? ' error' : ''}`}
                      value={sessionForm.topic}
                      onChange={onChangeS('topic')}
                    >
                      <option value="">-- Select Topic --</option>
                      {TRAINING_TOPICS.map((topic, index) => <option key={topic} value={String(index + 1)}>{topic}</option>)}
                    </select>
                  </Field>

                  <div className="form-row">
                    <Field id="ses_type" label="Session Type" error={sessionErrors.session_type}>
                      <select
                        id="ses_type"
                        className={`form-control${sessionErrors.session_type ? ' error' : ''}`}
                        value={sessionForm.session_type}
                        onChange={onChangeS('session_type')}
                      >
                        <option value="">-- Select --</option>
                        {SESSION_TYPES.map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </Field>
                    <Field id="ses_time" label="Preferred Session Time" error={sessionErrors.session_time}>
                      <select
                        id="ses_time"
                        className={`form-control${sessionErrors.session_time ? ' error' : ''}`}
                        value={sessionForm.session_time}
                        onChange={onChangeS('session_time')}
                      >
                        <option value="">-- Select --</option>
                        {TIMING_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    </Field>
                  </div>

                  <Field
                    id="ses_description"
                    label="Your Goals & Background"
                    error={sessionErrors.description}
                    hint="The more detail you provide, the better we can match you with the right instructor."
                  >
                    <textarea
                      id="ses_description"
                      className={`form-control${sessionErrors.description ? ' error' : ''}`}
                      rows={4}
                      placeholder="E.g., I'm a 2-year .NET developer looking to transition into cloud architecture. I know C# well but need guidance on AWS services..."
                      value={sessionForm.description}
                      onChange={onChangeS('description')}
                    />
                  </Field>

                  <button type="submit" className="btn btn-orange btn-full btn-lg">
                    Book My Session →
                  </button>
                </form>
              </div>
            ) : (
              <div style={SUCCESS_CARD_STYLE}>
                <div style={{ fontSize: '3.5rem', marginBottom: '14px' }}>🎉</div>
                <h2 style={{ fontSize: '1.4rem', fontWeight: 800, color: 'var(--gray-800)', marginBottom: '8px' }}>
                  Session Request Received!
                </h2>
                <p style={{ color: 'var(--gray-600)', maxWidth: '440px', margin: '0 auto 24px' }}>
                  Amazing! Our team will contact you within <strong>4 hours</strong> to confirm your session details
                  and introduce your instructor. Get ready to learn!
                </p>
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                  <Link to="/" className="btn btn-blue">Back to Home</Link>
                  <button className="btn btn-outline-gray" onClick={resetSession}>Book Another</button>
                </div>
              </div>
            )
          )}

        </div>
      </div>
    </Layout>
  );
};

export default RequestPage;
